<template>
  <div class="tw:hidden tw:sm:flex tw:relative tw:w-full" :key="selectedAccommodationId">
    <LaunchpadLoader
      v-if="isLoading || !hasStudioPlus"
      class="tw:w-full tw:flex tw:justify-center tw:items-center tw:flex-1"
    />
    <div v-else-if="isError" class="tw:flex tw:justify-center tw:items-center tw:flex-1">
      <ErrorModal
        :allow-retry="false"
        :app-name="'Rate Insights'"
        :use-app-name="true"
        header-key="onboarding.appLaunchError.header"
        content-key="onboarding.appLaunchError.text"
        retry-key="onboarding.appLaunchError.retryButton"
      />
    </div>
    <router-view v-else />
    <FullScreenLoader v-show="isRIDataFetching || isAuthFetching" />
  </div>
</template>

<script setup lang="ts">
/**
 * Rate Insights Layout Component
 *
 * Access control (Plus subscription check) handled by route meta:
 *   meta: { requires: { hasStudioPlus: true } }
 *
 * This component handles:
 * - Loading state for RI-specific data (channels, competitors)
 * - Routing between onboarding/view based on competitor availability
 * - Plus subscription auth via useStudioPlusAuth
 */
import { computed, watch, type ComputedRef } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { useSelectedBookingSites, useCompetitors } from '@/components/rateInsights/queries'
import { useStudioPlusAuth } from '@/composables/useStudioPlusAuth'
import { selectedAccommodationId } from '@/layouts/queries'

import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'
import ErrorModal from '@/components/dashboard/ErrorModal.vue'

const router = useRouter()
const route = useRoute()

const { isSelectedChannelsLoading, isSelectedChannelsFetching } = useSelectedBookingSites()
const { isCompetitorsLoading, competitors, isCompetitorsFetching, isCompetitorsError } =
  useCompetitors(selectedAccommodationId)

const { isAuthLoading, isAuthFetching, isError, hasStudioPlus } = useStudioPlusAuth()

/** Combined loading state from auth, channels, and competitors */
const isLoading: ComputedRef<boolean> = computed(
  () => isAuthLoading.value || isSelectedChannelsLoading.value || isCompetitorsLoading.value
)

/** Combined fetching state for RI data */
const isRIDataFetching: ComputedRef<boolean> = computed(
  () => isSelectedChannelsFetching.value || isCompetitorsFetching.value
)

/** Check if accommodation has competitors configured */
const hasCompetitors: ComputedRef<boolean> = computed(
  () => competitors?.value?.length > 0 && competitors.value !== undefined && !isCompetitorsError.value
)

/**
 * RI-specific routing: redirect to onboarding or view based on competitors
 * - No competitors → /rate-insights/onboarding
 * - Has competitors → /rate-insights/view
 */
const redirectToCorrectRIPage = (): void => {
  if (!hasStudioPlus.value) return

  const currentRoute = route.name

  // Redirect to onboarding if no competitors
  if (
    (currentRoute === 'rate-insights' && !hasCompetitors.value) ||
    (currentRoute === 'rate-insights-view' && !hasCompetitors.value)
  ) {
    router.replace('/rate-insights/onboarding')
  }
  // Redirect to view if has competitors
  else if (
    (currentRoute === 'rate-insights' && hasCompetitors.value) ||
    (currentRoute === 'rate-insights-onboarding' && hasCompetitors.value)
  ) {
    router.replace('/rate-insights/view')
  }
}

/**
 * Run RI routing after all data is loaded
 * Watches loading state and route changes to ensure correct page
 */
watch(
  [isLoading, () => route.name],
  ([newLoadingVal]: [boolean, string | symbol | null | undefined]) => {
    if (!newLoadingVal) {
      redirectToCorrectRIPage()
    }
  },
  { immediate: true }
)
</script>
